/**
 * Charts in published reports.
 *
 * A report's content is the answer markdown. Its charts travel with it as the
 * same hidden CHARTS marker the Ask AI backend stores on a turn, appended to
 * the end, so every reader renders text and charts from one string.
 *
 * KEEP-IN-SYNC: SPBFinnLambda chart_builder.charts_marker (format, ">" escape).
 */
import { CHARTS_MARKER_RE } from "../utils/responseMarkers";
import { ChartSpec, validChartSpecs } from "./types";

/**
 * Stay under the report content cap: publishReport / recordReportRun and the
 * DB CHECK allow 400,000 bytes, and recordReportRun turns larger content into
 * a failed run. Charts are dropped (last first) before the answer text is.
 */
export const REPORT_CONTENT_MAX_BYTES = 395_000;

/** UTF-8 byte length (the DB cap counts octets), without TextEncoder. */
function utf8Length(text: string): number {
  let bytes = 0;
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
}

/** The hidden marker for `charts`; "" when there are none. */
export function chartsMarker(charts?: ChartSpec[]): string {
  if (!charts || charts.length === 0) return "";
  // ">" is escaped so "-->" can never end the comment early.
  return `\n<!-- CHARTS:${JSON.stringify(charts).replace(/>/g, "\\u003e")} -->`;
}

/** Report content for `text` plus as many of `charts` as fit under the cap. */
export function withCharts(text: string, charts?: ChartSpec[]): string {
  const body = text.trimEnd();
  const kept = [...(charts ?? [])];
  while (kept.length > 0) {
    const content = body + chartsMarker(kept);
    if (utf8Length(content) <= REPORT_CONTENT_MAX_BYTES) return content;
    kept.pop();
  }
  return body;
}

/** Split stored report content into its markdown and its charts. */
export function splitCharts(content: string): {
  text: string;
  charts?: ChartSpec[];
} {
  const match = content.match(CHARTS_MARKER_RE);
  if (!match) return { text: content };
  let charts: ChartSpec[] | undefined;
  try {
    const valid = validChartSpecs(JSON.parse(match[1]));
    charts = valid.length > 0 ? valid : undefined;
  } catch {
    charts = undefined;
  }
  return { text: content.replace(match[0], ""), charts };
}
